import React, { useState, FC, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import {
  DrawerType,
  useInsideDrawerActions,
  useInsideDrawerState,
} from '@src/shared/context/inside-drawer';
import {
  trackNotificationIconClickCloseEvent,
  trackNotificationIconClickOpenEvent,
} from '@src/shared/tracking/navigation';
import { useQueryClient } from 'react-query';
import { INotificationAPI } from '@src/shared/queries';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import DashboardMobileHeader from '@src/shared/layouts/Dashboard/components/Header/components/DashboardMobileHeader';
import MobileHeader from './components/MobileHeader';
import MobileNavigationDrawerContent from './components/MobileNavigationDrawer';
import UserPanelDrawer from './components/UserPanelDrawer';
import NotificationDrawer from './components/NotificationDrawer';
import LanguagePanelDrawer from './components/LanguagePanelDrawer';
import DesktopHeader from './components/DesktopHeader';
import EmptyHeader from './components/EmptyHeader';
import HeaderWrapper from './components/HeaderWrapper';
import HeaderWrapperSeparator from './components/HeaderWrapperSeparator';
import PIESideSheet from '@src/shared/components/PIESideSheet';
import { useTranslation } from 'react-i18next';
import useDashsboardAllowedCountry from '@src/shared/hooks/use-dashboard-allowed';
import { isDashboardAllowed } from '@src/shared/utils';
import PIEText from '@src/shared/components/PIEText';
import { useScroll } from '@src/shared/hooks/use-scroll';

interface IHeaderProps {
  showOnNavigation?: boolean;
  showLogo?: boolean;
  isPanel?: boolean;
  currentPath?: string;
  fixed?: string | boolean;
}

export interface IHeaderVariantProps {
  onShowMenu?: () => void;
  onShowNotificationMenu?: () => void;
  onShowUserMenu?: () => void;
  onShowLanguageMenu?: () => void;
  showUserPanel?: boolean;
  showLanguageSelector?: boolean;
  showNotification?: boolean;
  notificationCount: number;
  loading: boolean;
  fixed: string | Boolean | undefined;
}

export const Header: FC<IHeaderProps> = ({ fixed }) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { keycloakToken, restaurant } = useKeycloakUserState();
  const [isNotificationMenuEnabledInCurrentRoute, setIsNotificationMenuEnabledInCurrentRoute] =
    useState(false);
  const { isResolutionMobile } = useDeviceDetect();
  const notificationRestrictedUrls = [
    '/impressum',
    '/privacy',
    '/terms-of-use',
    '/cookie-statement',
    '/technologies',
    '/courier-overview',
    '/product-overview',
  ];

  const isNoConsentParameterExist = () => {
    return /[?&]no_consent=/.test(location.search);
  };
  const [showNotificationSideSheet, setShowNotificationSideSheet] = useState(false);
  const { setActiveInsideDrawer } = useInsideDrawerActions();
  const { activeDrawerState } = useInsideDrawerState();
  const items = queryClient.getQueryCache().find('notifications')?.state.data as INotificationAPI[];
  const isLoading =
    queryClient?.getQueryCache()?.find('notifications')?.state?.isInvalidated &&
    queryClient.getQueryCache().find('notifications')?.state.isFetching;
  const itemsTotal = items?.length;

  const { t } = useTranslation();
  const { scrollTopNative } = useScroll();

  useEffect(() => {
    setIsNotificationMenuEnabledInCurrentRoute(
      !notificationRestrictedUrls.includes(location.pathname)
    );
  }, []);

  useEffect(() => {
    setIsNotificationMenuEnabledInCurrentRoute(
      !notificationRestrictedUrls.includes(location.pathname)
    );
  }, [location]);

  const onShowNotificationMenuHandler = () => {
    setShowNotificationSideSheet(true);
  };

  const onShowMenuHandler = () => {
    setActiveInsideDrawer(DrawerType.MOBILE_NAVIGATION);
  };

  const onShowUserMenuHandler = () => {
    setActiveInsideDrawer(DrawerType.USER_PANEL);
  };

  const onShowLanguageMenuHandler = () => {
    setActiveInsideDrawer(DrawerType.LANGUAGE_LIST);
  };

  const renderCompatibleHeader = () => {
    const HeaderVariantProps = {
      showNotification: isNotificationMenuEnabledInCurrentRoute,
      showUserPanel: !!keycloakToken,
      showLanguageSelector: !keycloakToken,
      notificationCount: itemsTotal,
      loading: !!isLoading,
      onShowNotificationMenu: onShowNotificationMenuHandler,
      onShowMenu: onShowMenuHandler,
      onShowUserMenu: onShowUserMenuHandler,
      onShowLanguageMenu: onShowLanguageMenuHandler,
      fixed,
    };

    if (isNoConsentParameterExist()) {
      return <EmptyHeader />;
    }

    if (
      !!isDashboardAllowed(restaurant?.country as string) &&
      location.pathname === '/deliveries' &&
      isResolutionMobile
    ) {
      return <DashboardMobileHeader {...HeaderVariantProps} />;
    }

    return (
      <>
        {isResolutionMobile ? (
          <MobileHeader {...HeaderVariantProps} />
        ) : (
          <DesktopHeader {...HeaderVariantProps} />
        )}
      </>
    );
  };

  const calculateMarginTop = useCallback(() => {
    if (isResolutionMobile) {
      return !!isDashboardAllowed(restaurant?.country as string) || isResolutionMobile ? 0 : 56;
    }
    if (scrollTopNative < 56) {
      return 55 - scrollTopNative;
    } else if (scrollTopNative >= 56) {
      return 0;
    }
  }, [scrollTopNative, isResolutionMobile]);

  return (
    <>
      <HeaderWrapper
        fixed={
          !isResolutionMobile || !!isDashboardAllowed(restaurant?.country as string) ? false : fixed
        }
      >
        <MobileNavigationDrawerContent />
        <UserPanelDrawer />
        <LanguagePanelDrawer />
        {renderCompatibleHeader()}
      </HeaderWrapper>
      <HeaderWrapperSeparator fixed={fixed} />
      {showNotificationSideSheet && (
        <PIESideSheet
          title={
            <>
              <PIEText
                text={t('notification.notificationDrawerHeading')}
                size="var(--xds-font-size-20)"
                fontWeight="var(--xds-font-weight-bold)"
                color="var(--xds-color-content-default)"
              />
            </>
          }
          width={!isResolutionMobile ? 400 : 300}
          marginTop={calculateMarginTop()}
          isOpenInitial={true}
          onClose={() => {
            setShowNotificationSideSheet(false);
            trackNotificationIconClickCloseEvent('', restaurant?.country, restaurant?.language);
          }}
          onOpen={() => {
            trackNotificationIconClickOpenEvent('', restaurant?.country, restaurant?.language);
          }}
        >
          <NotificationDrawer />
        </PIESideSheet>
      )}
    </>
  );
};

export default Header;
